import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component, inject,
  Input,
  viewChild,
  viewChildren,
} from '@angular/core';
import { RouterLinkActive } from '@angular/router';

import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { GoogleAnalyticsService } from '@pt/services';
import { TextFormaterHelper } from '@pt/helpers';

import { NavItem } from './models/nav-item.model';

/**
 * Navbar tab
 */
@Component({
  standalone: false,
  selector: 'pu-nav-item',
  templateUrl: './nav-item.component.html',
  styleUrls: ['./nav-item.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavItemComponent implements AfterViewInit {
  routerLinkActiveDirective = viewChild.required(RouterLinkActive);
  navItemComponents = viewChildren(NavItemComponent);
  itemSubject = new BehaviorSubject<NavItem>(null);
  hasSubItems$ = this.itemSubject.pipe(map(({ subItems }) => subItems?.length > 0));
  readonly #googleAnalyticsService = inject(GoogleAnalyticsService);
  private _initContentSubject = new Subject<void>();
  isActive$ = this._initContentSubject.pipe(
    switchMap(() => this.hasSubItems$),
    switchMap(hasSubItems => (hasSubItems
      ? this._getChildIsActive()
      : this.routerLinkActiveDirective().isActiveChange.pipe(
        startWith(false),
      ))),
    // eslint-disable-next-line rxjs/no-sharereplay
    shareReplay(1),
  );

  /**
   * Item
   */
  @Input()
  set item(value: NavItem) {
    this.itemSubject.next(value);
  }

  ngAfterViewInit() {
    this._initContentSubject.next();
    this._initContentSubject.complete();
  }

  onItemClick(item: NavItem): void {
    const element_name = item.link === '/'
      ? 'home_page'
      : TextFormaterHelper.transformToSnakeCase(item.link?.replace('/', ''));

    this.#googleAnalyticsService.pushCustomEvent('menu', 'select_item', {
      element_name,
    });
  }

  private _getChildIsActive(): Observable<boolean> {
    return combineLatest(this.navItemComponents().map(component => component.isActive$)).pipe(map(
      conditions => conditions.some(Boolean),
    ));
  }
}
